import type { ParseKeys } from 'i18next'

import type { AccessRead } from '@/client'
import { can, type Permission } from '@/lib/access'

// The app's map: every section the rail shows and every page under it.
//
// Labels are translation keys, not text. A hardcoded string here is invisible
// to every future locale and to anyone searching for what a page is called.
//
// VentaWiz's own map, not VentaHub's menu: at most four sections a run, no
// page deeper than section › group › page, and no two rows with one name.
// VentaHub is a reference for what exists, not for where it goes.
//
// A page not built yet is `soon`. It keeps its place in the rail, so the map
// says what is coming, but it is not a link — the rail never offers a page
// that ends on not-found — and search leaves it out. `to` is a plain string
// for that reason: the router's typed paths only know routes that exist.
//
// A section is either a page of its own (`to`) or a folder of pages
// (`children`). Folders nest one level, and the sidebar draws them as a tree.
//
// `permission` is what the page lists with — the one its `RequireAccess`
// checks, or for a page not built yet, the module it will belong to. A page is
// offered to somebody holding it anywhere, since a store manager's page lists
// their stores; one with none is offered to everybody (the dashboard says per
// panel what is out of reach). A list is any one of them, for a page the
// backend opens to more than one module.

export type NavLink = {
  to: string
  key: ParseKeys
  icon?: string
  permission?: Permission | readonly Permission[]
  soon?: true
}
export type NavGroup = { key: ParseKeys; children: readonly NavNode[] }
export type NavNode = NavLink | NavGroup

/** A first-level entry: an icon in its section's own hue (`--nav-*`, design board 02). */
export type Section = { key: ParseKeys; icon: string; hue: string } & (
  | {
      to: string
      permission?: Permission | readonly Permission[]
      soon?: true
    }
  | { children: readonly NavNode[] }
)

/** The ungrouped head of the rail. */
const WORKSPACE: readonly Section[] = [
  { to: '/', key: 'nav.dashboard', icon: 'space_dashboard', hue: 'text-nav-dashboard' },
  // One page with two tabs, Business and Ads. Raising a ticket is that page's
  // primary button.
  {
    to: '/tickets',
    permission: 'tickets:view',
    key: 'nav.tickets',
    icon: 'confirmation_number',
    hue: 'text-nav-tickets',
  },
]

/** "Analyse": where the data is read. */
const ANALYSE: readonly Section[] = [
  {
    key: 'nav.focusedSkus',
    icon: 'target',
    hue: 'text-nav-focused',
    children: [
      {
        icon: 'filter_center_focus',
        to: '/focused-data/manage-skus',
        permission: 'catalogue:view',
        key: 'nav.focusedSkusItems.manage',
      },
      {
        icon: 'insert_chart',
        to: '/focused-data/analyzer',
        permission: 'catalogue:view',
        key: 'nav.focusedSkusItems.analysis',
      },
      {
        icon: 'manage_search',
        to: '/focused-data/search-term/sheet',
        permission: 'ads:view',
        key: 'nav.focusedSkusItems.searchTerm',
      },
      {
        icon: 'add_notes',
        to: '/focused-data/search-term',
        permission: 'ads:edit',
        key: 'nav.focusedSkusItems.searchTermData',
      },
    ],
  },
  {
    key: 'nav.marketing',
    icon: 'campaign',
    hue: 'text-nav-marketing',
    // Read, then automate, then set up: the order a week of ads work runs in.
    children: [
      {
        key: 'nav.marketingItems.performance',
        children: [
          {
            icon: 'insights',
            to: '/marketing/ams',
            permission: 'ads:view',
            key: 'nav.marketingItems.amsView',
          },
          {
            icon: 'history',
            to: '/marketing/ams/history',
            permission: 'ads:view',
            key: 'nav.marketingItems.amsHistory',
          },
          {
            icon: 'fact_check',
            to: '/marketing/review-ams-data',
            permission: 'ads:view',
            key: 'nav.marketingItems.reviewAmsData',
          },
          {
            icon: 'query_stats',
            to: '/marketing/keyword-performance',
            permission: 'ads:view',
            key: 'nav.marketingItems.searchAnalytics',
          },
          // The accuracy page stays out of the menu: the simulator links to it.
          {
            icon: 'tune',
            to: '/marketing/ams/bid-simulator',
            permission: 'ads:view',
            key: 'nav.marketingItems.bidSimulator',
          },
        ],
      },
      {
        key: 'nav.marketingItems.automation',
        children: [
          {
            icon: 'checklist',
            to: '/marketing/rules-v2',
            permission: 'ads:view',
            key: 'nav.marketingItems.rules',
          },
          {
            icon: 'play_circle',
            to: '/marketing/rules-v2/executions',
            permission: 'ads:view',
            key: 'nav.marketingItems.executions',
          },
          {
            icon: 'monitor_heart',
            to: '/marketing/rules-v2/health',
            permission: 'ads:view',
            key: 'nav.marketingItems.rulesHealth',
          },
          // What the rules leave alone, beside the rules.
          {
            icon: 'do_not_disturb_on',
            to: '/marketing/rules-v2/exclusions',
            permission: 'ads:view',
            key: 'nav.exceptionsItems.ruleExclusions',
          },
          {
            icon: 'schedule',
            to: '/marketing/dayparting',
            permission: 'ads:view',
            key: 'nav.marketingItems.dayparting',
          },
          {
            icon: 'auto_fix_high',
            to: '/marketing/optimization',
            permission: 'ads:view',
            key: 'nav.marketingItems.optimization',
          },
        ],
      },
      {
        key: 'nav.marketingItems.setup',
        children: [
          {
            icon: 'table_view',
            to: '/marketing/master-files/amazon',
            permission: 'ads:view',
            key: 'nav.marketingItems.adGroupMaster',
          },
          {
            icon: 'my_location',
            to: '/marketing/new-targeting',
            permission: 'ads:view',
            key: 'nav.marketingItems.newTargeting',
          },
          {
            icon: 'sync',
            to: '/marketing/report-sync',
            permission: 'ads:view',
            key: 'nav.marketingItems.reportSync',
          },
        ],
      },
    ],
  },
  {
    key: 'nav.business',
    icon: 'storefront',
    hue: 'text-nav-business',
    children: [
      {
        icon: 'leaderboard',
        to: '/business/view',
        permission: 'orders:view',
        key: 'nav.businessItems.businessView',
      },
      {
        icon: 'bolt',
        to: '/business/seller-orders',
        permission: 'orders:view',
        key: 'nav.businessItems.sellerOrders',
      },
      {
        icon: 'trending_down',
        to: '/business/loss-making-skus',
        permission: 'finance:view',
        key: 'nav.businessItems.lossMakingSkus',
      },
      {
        icon: 'handshake',
        to: '/business/partner-view',
        permission: 'finance:view',
        key: 'nav.businessItems.partnerView',
      },
      {
        icon: 'currency_exchange',
        to: '/business/payment-reconciliation',
        permission: 'finance:view',
        key: 'nav.businessItems.paymentReconciliation',
      },
      {
        icon: 'rate_review',
        to: '/business/review-data',
        permission: 'orders:view',
        key: 'nav.businessItems.reviewData',
      },
    ],
  },
  {
    key: 'nav.dataVault',
    icon: 'database',
    hue: 'text-nav-vault',
    children: [
      {
        key: 'nav.dataVaultItems.catalogue',
        children: [
          {
            icon: 'sell',
            to: '/data-vault/products',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.products',
          },
          {
            icon: 'inventory_2',
            to: '/data-vault/internal-skus',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.internalSkus',
          },
          {
            icon: 'price_check',
            to: '/data-vault/costs',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.costs',
          },
          {
            icon: 'percent',
            to: '/data-vault/fees',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.fees',
          },
          {
            icon: 'price_change',
            to: '/data-vault/product-price-sync',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.productPriceSync',
          },
          {
            icon: 'travel_explore',
            to: '/data-vault/marketplace-data',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.marketplaceData',
          },
        ],
      },
      {
        key: 'nav.dataVaultItems.ordersPayouts',
        children: [
          {
            icon: 'shopping_cart',
            to: '/data-vault/orders',
            permission: 'orders:view',
            key: 'nav.dataVaultItems.orders',
          },
          {
            icon: 'assignment_return',
            to: '/data-vault/returns',
            permission: 'orders:view',
            key: 'nav.dataVaultItems.returns',
          },
          {
            icon: 'account_balance',
            to: '/data-vault/settlements',
            permission: 'orders:view',
            key: 'nav.dataVaultItems.settlements',
          },
        ],
      },
      // What is held back from tickets and alerts on purpose, found as a
      // control beside the data it filters.
      {
        key: 'nav.exceptions',
        children: [
          {
            icon: 'mark_email_unread',
            to: '/data-vault/mail-exceptions',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.mailExceptions',
          },
          {
            icon: 'rule',
            to: '/data-vault/ticket-exceptions',
            permission: 'catalogue:view',
            key: 'nav.dataVaultItems.ticketExceptions',
          },
        ],
      },
      // What the scrapers read off the storefronts rather than the APIs. One
      // page, with Reviews and Ads as its tabs.
      // The backend reads it for catalogue or ads access.
      {
        icon: 'web',
        to: '/scraped-data',
        permission: ['catalogue:view', 'ads:view'],
        key: 'nav.scrapedData',
      },
    ],
  },
]

/** "Run": operations and tools. */
const RUN: readonly Section[] = [
  {
    key: 'nav.operations',
    icon: 'precision_manufacturing',
    hue: 'text-nav-ops',
    children: [
      {
        key: 'nav.opsItems.stock',
        children: [
          // Hidden from the sidebar for now at the user's request; the route and
          // page are untouched, so a direct link to /inventory-sync still works.
          {
            icon: 'block',
            to: '/operations/block-stock',
            permission: 'operations:view',
            key: 'nav.opsItems.blockStock',
          },
          {
            icon: 'restart_alt',
            to: '/operations/replen',
            permission: 'operations:view',
            key: 'nav.opsItems.replen',
          },
          {
            icon: 'warehouse',
            to: '/operations/seller-flex-stock',
            permission: 'operations:view',
            key: 'nav.opsItems.sellerFlexStock',
          },
          {
            icon: 'inventory',
            to: '/operations/get-stock',
            permission: 'operations:view',
            key: 'nav.opsItems.getStock',
          },
          {
            icon: 'hourglass_bottom',
            to: '/operations/stock-ageing',
            permission: 'operations:view',
            key: 'nav.opsItems.stockAgeing',
          },
          // Warehouse transfer now lives inside Block stock, as one of its own
          // actions, matching VentaHub. The standalone page's route still works
          // via a direct link; only this sidebar entry is removed.
        ],
      },
      {
        key: 'nav.opsItems.shipments',
        children: [
          {
            icon: 'package_2',
            to: '/operations/box-detail',
            permission: 'operations:view',
            key: 'nav.opsItems.boxDetail',
          },
          {
            icon: 'manage_history',
            to: '/operations/box-history',
            permission: 'operations:view',
            key: 'nav.opsItems.boxHistory',
          },
          {
            icon: 'public',
            to: '/operations/global-shipments',
            permission: 'operations:view',
            key: 'nav.opsItems.globalShipments',
          },
        ],
      },
      {
        key: 'nav.opsItems.purchasing',
        children: [
          {
            icon: 'autorenew',
            to: '/operations/auto-ordering',
            permission: 'operations:view',
            key: 'nav.opsItems.autoOrdering',
          },
          {
            icon: 'local_shipping',
            to: '/operations/po-fulfilment',
            permission: 'operations:view',
            key: 'nav.opsItems.poFulfilment',
          },
          {
            icon: 'shopping_bag',
            to: '/operations/purchase-orders',
            permission: 'operations:view',
            key: 'nav.opsItems.purchaseOrders',
          },
          {
            icon: 'assignment',
            to: '/operations/vendor-orders',
            permission: 'operations:view',
            key: 'nav.opsItems.vendorOrders',
          },
          {
            icon: 'request_page',
            to: '/operations/vendor-invoices',
            permission: 'operations:view',
            key: 'nav.opsItems.vendorInvoices',
          },
          {
            icon: 'paid',
            to: '/operations/vendor-payments',
            permission: 'operations:view',
            key: 'nav.opsItems.vendorPayments',
          },
        ],
      },
      {
        key: 'nav.opsItems.financeDocs',
        children: [
          {
            icon: 'description',
            to: '/operations/sales-invoices',
            permission: 'finance:view',
            key: 'nav.opsItems.salesInvoices',
          },
          {
            icon: 'request_quote',
            to: '/operations/credit-debit-notes',
            permission: 'finance:view',
            key: 'nav.opsItems.creditDebitNotes',
          },
          {
            icon: 'receipt',
            to: '/operations/retailer-receipts',
            permission: 'finance:view',
            key: 'nav.opsItems.retailerReceipts',
          },
          {
            icon: 'receipt_long',
            to: '/operations/sp-invoices',
            permission: 'finance:view',
            key: 'nav.opsItems.spInvoices',
          },
          {
            icon: 'payments',
            to: '/operations/sp-payments',
            permission: 'finance:view',
            key: 'nav.opsItems.spPayments',
          },
          {
            icon: 'savings',
            to: '/operations/cashflow',
            permission: 'finance:view',
            key: 'nav.opsItems.cashflow',
          },
          {
            icon: 'task',
            to: '/operations/review',
            permission: 'operations:view',
            key: 'nav.opsItems.review',
          },
        ],
      },
    ],
  },
  { to: '/ai-listing', key: 'nav.aiListing', icon: 'auto_awesome', hue: 'text-nav-ai' },
  // The full page; the same analyst is also docked on every page.
  {
    to: '/ai-analyst',
    permission: ['ai_analyst:view', 'ai_analyst:edit'],
    key: 'nav.aiAnalyst',
    icon: 'neurology',
    hue: 'text-nav-analyst',
  },
]

/** The sidebar's runs of sections, each under its overline (the first has none). */
export const SECTION_RUNS: readonly { key?: ParseKeys; sections: readonly Section[] }[] = [
  { sections: WORKSPACE },
  { key: 'nav.groups.analyse', sections: ANALYSE },
  { key: 'nav.groups.run', sections: RUN },
]

/** The sidebar's foot: the community and the person's own page. */
export const FOOTER: readonly Section[] = [
  // Open to everybody: somebody with no access at all is who needs it most.
  { to: '/access', key: 'nav.myAccess', icon: 'key', hue: 'text-nav-profile' },
  { to: '/forum', soon: true, key: 'nav.forum', icon: 'forum', hue: 'text-nav-forum' },
]

const ALL_SECTIONS: readonly Section[] = [...WORKSPACE, ...ANALYSE, ...RUN, ...FOOTER]

export function isGroup(node: NavNode): node is NavGroup {
  return 'children' in node
}

export function hasPages(section: Section): section is Section & { children: readonly NavNode[] } {
  return 'children' in section
}

/**
 * Whether `pathname` is the page at `to` or one beneath it.
 *
 * `/` is matched exactly: it is a prefix of every path, and without that the
 * dashboard would claim every page in the app.
 */
export function matchesPath(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * Asked of the links, not of a path prefix, so a section is never tied to the
 * first segment of its pages' URLs. A page not built yet holds no path: its
 * address ends on not-found, which is no place in the map.
 */
export function containsPath(nodes: readonly NavNode[], pathname: string): boolean {
  return nodes.some((node) =>
    isGroup(node)
      ? containsPath(node.children, pathname)
      : !node.soon && matchesPath(node.to, pathname),
  )
}

/** Whether every page under a group is still to come: the group carries the one tag. */
export function allSoon(nodes: readonly NavNode[]): boolean {
  return nodes.every((node) => (isGroup(node) ? allSoon(node.children) : node.soon === true))
}

/** The section a path belongs to, or undefined for a page outside the map. */
export function sectionFor(pathname: string): Section | undefined {
  return ALL_SECTIONS.find((section) =>
    hasPages(section)
      ? containsPath(section.children, pathname)
      : !section.soon && matchesPath(section.to, pathname),
  )
}

/** A titled run of links in the panel. The section's own links have no title. */
export type PanelGroup = { key?: ParseKeys; links: readonly NavLink[] }

/**
 * A section's pages as flat, titled runs.
 *
 * The section's direct links come first, untitled. Every nested group,
 * however deep, becomes its own run under its own name, its direct links
 * before the groups inside it — so Advertising › Automation reads as
 * "Automation" over its pages, never as a second indent.
 */
export function panelGroups(section: Section): PanelGroup[] {
  if (!hasPages(section)) return [{ links: [{ to: section.to, key: section.key }] }]

  const runs: PanelGroup[] = []
  const walk = (nodes: readonly NavNode[], key?: ParseKeys) => {
    const links = nodes.filter((node): node is NavLink => !isGroup(node))
    if (links.length > 0) runs.push({ key, links })
    for (const node of nodes) if (isGroup(node)) walk(node.children, node.key)
  }
  walk(section.children)
  return runs
}

/**
 * Where a path sits, for the header's trail: its section, the titled run it
 * is in (if any) and the page itself.
 *
 * The page is the longest matching link, so `/marketing/ams/history` is
 * Change history rather than AMS View, and a detail route under a list
 * (`/tickets/42`) still names the list it came from.
 */
export function trailFor(
  pathname: string,
): { section: Section; group?: ParseKeys; page?: NavLink } | undefined {
  const section = sectionFor(pathname)
  if (!section) return undefined
  if (!hasPages(section)) return { section }

  let best: { group?: ParseKeys; page: NavLink } | undefined
  for (const run of panelGroups(section)) {
    for (const link of run.links) {
      if (
        !link.soon &&
        matchesPath(link.to, pathname) &&
        link.to.length > (best?.page.to.length ?? -1)
      ) {
        best = { group: run.key, page: link }
      }
    }
  }
  return { section, ...best }
}

/** A page anywhere in the map, with the folders above it — what search lists. */
export type PageEntry = {
  to: string
  key: ParseKeys
  icon: string
  hue: string
  trail: ParseKeys[]
}

/**
 * Whether a page is offered. Until the access summary arrives — or if it
 * cannot be read — everything is: hiding is UX, the backend decides, and a
 * sidebar that empties itself on a slow request is worse than one that offers
 * a page which then says no.
 */
function offered(
  access: AccessRead | undefined,
  permission: Permission | readonly Permission[] | undefined,
): boolean {
  if (access === undefined || permission === undefined) return true
  return (typeof permission === 'string' ? [permission] : permission).some((each) =>
    can(access, each),
  )
}

/** The pages offered, and the folders that still hold one. */
function visibleNodes(nodes: readonly NavNode[], access: AccessRead | undefined): NavNode[] {
  return nodes.flatMap((node): NavNode[] => {
    if (!isGroup(node)) return offered(access, node.permission) ? [node] : []
    const children = visibleNodes(node.children, access)
    return children.length > 0 ? [{ ...node, children }] : []
  })
}

function visibleSections(sections: readonly Section[], access: AccessRead | undefined): Section[] {
  return sections.flatMap((section): Section[] => {
    if (!hasPages(section)) return offered(access, section.permission) ? [section] : []
    const children = visibleNodes(section.children, access)
    return children.length > 0 ? [{ ...section, children }] : []
  })
}

/**
 * The sidebar's runs as this reader sees them: a page needing a permission
 * they hold nowhere is left out, a folder left empty goes with it, and so
 * does a run with no section left. A page not built yet stays, as a promise.
 */
export function visibleRuns(access: AccessRead | undefined): typeof SECTION_RUNS {
  return SECTION_RUNS.flatMap((run) => {
    const sections = visibleSections(run.sections, access)
    return sections.length > 0 ? [{ ...run, sections }] : []
  })
}

/**
 * The foot of the rail as this reader sees it. Unlike the runs, nothing
 * waits for the access summary here: a footer row with a permission is shown
 * only once the reader is known to hold it.
 */
export function visibleFooter(access: AccessRead | undefined): Section[] {
  return FOOTER.filter(
    (section) =>
      hasPages(section) ||
      section.permission === undefined ||
      (access !== undefined && offered(access, section.permission)),
  )
}

/**
 * Every page the reader is offered and can open, in the sidebar's order.
 * Search goes to a page, so a page not built yet is not one of its answers.
 */
export function allPages(access?: AccessRead): PageEntry[] {
  const pages: PageEntry[] = []
  const walk = (nodes: readonly NavNode[], section: Section, trail: ParseKeys[]) => {
    for (const node of nodes) {
      if (isGroup(node)) walk(node.children, section, [...trail, node.key])
      else if (!node.soon)
        pages.push({
          to: node.to,
          key: node.key,
          icon: node.icon ?? section.icon,
          hue: section.hue,
          trail,
        })
    }
  }
  const sections = [...visibleRuns(access).flatMap((run) => run.sections), ...visibleFooter(access)]
  for (const section of sections) {
    if (hasPages(section)) walk(section.children, section, [section.key])
    else if (!section.soon)
      pages.push({
        to: section.to,
        key: section.key,
        icon: section.icon,
        hue: section.hue,
        trail: [],
      })
  }
  return pages
}
